import { useState, useEffect, useRef, useCallback } from 'react'
import { XMarkIcon, ChatBubbleLeftRightIcon, PaperAirplaneIcon, PaperClipIcon } from '@heroicons/react/24/outline'
import api from '../utils/api'
import logger from '../utils/logger'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { usePolling } from '../hooks/usePolling'

const SupportChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [tickets, setTickets] = useState([])
  const [activeTicket, setActiveTicket] = useState(null)
  const [message, setMessage] = useState('')
  const [subject, setSubject] = useState('')
  const [isNewTicket, setIsNewTicket] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [pendingFiles, setPendingFiles] = useState([]) // attachments selected but not yet sent
  const messagesEndRef = useRef(null)
  const pollingIntervalRef = useRef(null)
  const fileInputRef = useRef(null)

  // Загрузить тикеты при открытии
  useEffect(() => {
    if (isOpen) {
      loadTickets()
    }
  }, [isOpen])

  // Автопрокрутка к последнему сообщению
  useEffect(() => {
    scrollToBottom()
  }, [activeTicket?.messages])

  // Polling для обновления сообщений
  useEffect(() => {
    if (isOpen && activeTicket) {
      startPolling()
    } else {
      stopPolling()
    }
    return () => stopPolling()
  }, [isOpen, activeTicket])

  // Проверка непрочитанных сообщений когда виджет закрыт.
  // usePolling пропускает tick когда navigator.onLine === false и делает
  // exponential backoff на consecutive failures — раньше setInterval
  // бил каждые 30с даже без сети, плодя 100+ зависших запросов за час
  // отсутствия интернета (видно в console-логе у юзера: накопленный
  // таймаут на одном request достигал 2 660 283 мс ≈ 44 минуты).
  // Чтобы callback был стабильной ссылкой, оборачиваем в useCallback.
  const checkUnreadMessages = useCallback(async () => {
    try {
      const response = await api.get('/support?status=open')
      const openTickets = response.data.data || []
      let totalUnread = 0
      openTickets.forEach(ticket => {
        ticket.messages.forEach(msg => {
          if (msg.sender === 'admin' && !msg.read) {
            totalUnread++
          }
        })
      })
      setUnreadCount(totalUnread)
    } catch (error) {
      logger.error('Failed to check unread messages:', error)
      // throw чтобы usePolling зафиксировал failure и применил backoff
      throw error
    }
  }, [])

  usePolling(checkUnreadMessages, 30000, { enabled: !isOpen, runImmediately: true })

  const startPolling = () => {
    stopPolling()
    pollingIntervalRef.current = setInterval(() => {
      if (activeTicket) {
        loadTicket(activeTicket._id)
      }
    }, 5000) // каждые 5 секунд
  }

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const loadTickets = async () => {
    try {
      const response = await api.get('/support?status=open')
      setTickets(response.data.data || [])
    } catch (error) {
      logger.error('Failed to load tickets:', error)
    }
  }

  const loadTicket = async (ticketId) => {
    try {
      const response = await api.get(`/support/${ticketId}`)
      setActiveTicket(response.data.data)
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (error) {
      logger.error('Failed to load ticket:', error)
    }
  }

  const createTicket = async () => {
    if (!subject.trim() || !message.trim()) return

    setIsLoading(true)
    try {
      const response = await api.post('/support', {
        subject: subject.trim(),
        message: message.trim()
      })
      setActiveTicket(response.data.data)
      setTickets(prev => [response.data.data, ...prev])
      setSubject('')
      setMessage('')
      setIsNewTicket(false)
    } catch (error) {
      logger.error('Failed to create ticket:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const sendMessage = async () => {
    if (!activeTicket) return
    if (!message.trim() && pendingFiles.length === 0) return

    setIsLoading(true)
    try {
      // Always send as multipart — simpler than branching on presence of files,
      // and the backend accepts both (empty files → plain text message).
      const formData = new FormData()
      formData.append('message', message.trim())
      pendingFiles.forEach(f => formData.append('files', f))

      const response = await api.post(`/support/${activeTicket._id}/messages`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setActiveTicket(response.data.data)
      setMessage('')
      setPendingFiles([])

      // Обновить список тикетов
      setTickets(prev => prev.map(t =>
        t._id === response.data.data._id ? response.data.data : t
      ))
    } catch (error) {
      logger.error('Failed to send message:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleFilePick = (e) => {
    const picked = Array.from(e.target.files || []).slice(0, 5 - pendingFiles.length)
    const valid = picked.filter(f => f.type.startsWith('image/') && f.size <= 10 * 1024 * 1024)
    setPendingFiles(prev => [...prev, ...valid])
    e.target.value = '' // allow re-selecting the same file later
  }

  const removePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx))
  }

  const closeTicket = async () => {
    if (!activeTicket) return

    try {
      await api.post(`/support/${activeTicket._id}/close`)
      setActiveTicket(null)
      loadTickets()
    } catch (error) {
      logger.error('Failed to close ticket:', error)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (isNewTicket) {
        createTicket()
      } else {
        sendMessage()
      }
    }
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 bg-brand-lime hover:bg-brand-lime-600 text-brand-black rounded-full p-3 sm:p-4 shadow-lg transition-all hover:scale-110 z-50"
      >
        <ChatBubbleLeftRightIcon className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
    )
  }

  return (
    <div className="fixed inset-0 sm:inset-auto sm:bottom-6 sm:right-6 w-full h-full sm:w-96 sm:h-[600px] bg-white rounded-none sm:rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-lime to-brand-lime-600 text-brand-black p-4 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg">Поддержка</h3>
          <p className="text-sm opacity-80">Мы онлайн</p>
        </div>
        <button
          onClick={() => {
            setIsOpen(false)
            setActiveTicket(null)
            setIsNewTicket(false)
          }}
          className="p-2 hover:bg-black/10 rounded-lg transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {!activeTicket && !isNewTicket ? (
          // Список тикетов
          <div className="p-4">
            <button
              onClick={() => setIsNewTicket(true)}
              className="w-full bg-brand-lime hover:bg-brand-lime-600 text-brand-black font-semibold py-3 rounded-xl mb-4 transition-all"
            >
              Новое обращение
            </button>

            {tickets.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <ChatBubbleLeftRightIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>У вас пока нет открытых обращений</p>
              </div>
            ) : (
              <div className="space-y-2">
                {tickets.map(ticket => (
                  <button
                    key={ticket._id}
                    onClick={() => loadTicket(ticket._id)}
                    className="w-full text-left p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <div className="font-semibold text-slate-900 mb-1 truncate">
                      {ticket.subject}
                    </div>
                    <div className="text-sm text-slate-500">
                      {format(new Date(ticket.lastMessageAt), 'dd MMM, HH:mm', { locale: ru })}
                    </div>
                    {ticket.messages.some(m => m.sender === 'admin' && !m.read) && (
                      <span className="inline-block mt-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                        Новое
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : isNewTicket ? (
          // Форма нового тикета
          <div className="p-4">
            <button
              onClick={() => setIsNewTicket(false)}
              className="text-slate-600 hover:text-slate-900 mb-4 text-sm"
            >
              ← Назад
            </button>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Тема обращения
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Кратко опишите проблему"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-lime"
                  maxLength={200}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Сообщение
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Опишите вашу проблему подробнее..."
                  rows={6}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-lime resize-none"
                  maxLength={2000}
                />
              </div>
            </div>
          </div>
        ) : (
          // Чат с активным тикетом
          <div className="flex flex-col h-full">
            <div className="p-3 border-b border-slate-200 flex items-center justify-between">
              <button
                onClick={() => setActiveTicket(null)}
                className="text-slate-600 hover:text-slate-900 text-sm"
              >
                ← Назад
              </button>
              <button
                onClick={closeTicket}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                Закрыть обращение
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <div className="text-center mb-4">
                <div className="text-sm font-semibold text-slate-700">{activeTicket.subject}</div>
                <div className="text-xs text-slate-500">
                  {format(new Date(activeTicket.createdAt), 'dd MMMM yyyy, HH:mm', { locale: ru })}
                </div>
              </div>

              {activeTicket.messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                      msg.sender === 'user'
                        ? 'bg-brand-lime text-brand-black'
                        : 'bg-slate-100 text-slate-900'
                    }`}
                  >
                    <div className="text-xs opacity-70 mb-1">{msg.senderName}</div>
                    {msg.text && (
                      <div className="whitespace-pre-wrap break-words">{msg.text}</div>
                    )}
                    {msg.attachments?.length > 0 && (
                      <div className={`grid gap-1.5 ${msg.text ? 'mt-2' : ''} ${msg.attachments.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                        {msg.attachments.map((att, i) => (
                          <a
                            key={i}
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block rounded-lg overflow-hidden border border-white/20 hover:opacity-90 transition"
                          >
                            <img
                              src={att.url}
                              alt={att.filename}
                              className="w-full h-auto max-h-48 object-cover"
                              loading="lazy"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                    <div className="text-xs opacity-60 mt-1">
                      {format(new Date(msg.timestamp), 'HH:mm', { locale: ru })}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      {(activeTicket || isNewTicket) && (
        <div className="p-4 border-t border-slate-200 bg-white">
          {/* Attachment previews — thumbnails of picked-but-not-yet-sent files */}
          {!isNewTicket && pendingFiles.length > 0 && (
            <div className="flex gap-2 mb-2 overflow-x-auto">
              {pendingFiles.map((f, idx) => (
                <div key={idx} className="relative shrink-0">
                  <img
                    src={URL.createObjectURL(f)}
                    alt={f.name}
                    className="w-16 h-16 object-cover rounded-lg border border-slate-200"
                  />
                  <button
                    onClick={() => removePendingFile(idx)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-zinc-900 text-white flex items-center justify-center shadow hover:bg-red-600 transition"
                    title="Убрать"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            {/* Attach screenshots — hidden on new-ticket form (нужно сначала создать тикет) */}
            {!isNewTicket && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                  multiple
                  className="hidden"
                  onChange={handleFilePick}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading || pendingFiles.length >= 5}
                  className="p-3 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  title="Прикрепить скриншот"
                >
                  <PaperClipIcon className="w-5 h-5" />
                </button>
              </>
            )}

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isNewTicket ? "Опишите проблему..." : "Введите сообщение..."}
              rows={2}
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-lime resize-none"
              maxLength={2000}
              disabled={isLoading}
            />
            <button
              onClick={isNewTicket ? createTicket : sendMessage}
              disabled={isLoading || (isNewTicket ? (!message.trim() || !subject.trim()) : (!message.trim() && pendingFiles.length === 0))}
              className="bg-brand-lime hover:bg-brand-lime-600 text-brand-black p-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PaperAirplaneIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default SupportChatWidget
